import { FC, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { cn } from "@/lib/utils";
import { 
  Settings2, 
  Users, 
  BookOpen,
  LayoutDashboard,
  LogOut,
  User,
  Menu,
  Wallet,
  Users as UsersIcon,
  Handshake,
  Book,
  MessageSquare
} from 'lucide-react';
import { Button } from '../ui/button';
import { useSidebar } from '../ui/sidebar';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useMediaQuery } from '@/hooks/use-media-query';

export const AdminSidebar: FC = () => {
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [isOpen, setIsOpen] = useState(false);

  const handleLogout = async () => {
    try {
      console.log('Starting admin logout process...');
      
      // Clear any existing session data from localStorage
      localStorage.removeItem('supabase.auth.token');
      
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('Admin logout error:', error);
        
        // If session not found or 403, just redirect to login
        if (error.message?.includes('session_not_found') || error.status === 403) {
          console.log('Admin session already expired, redirecting to login...');
          window.location.href = '/login';
          return;
        }
        
        toast.error('Error al cerrar sesión');
        return;
      }
      
      console.log('Admin logout successful');
      toast.success('Sesión cerrada correctamente');
      
      // Add a small delay before redirect to ensure toast is visible
      setTimeout(() => {
        navigate('/login');
      }, 500);
      
    } catch (error) {
      console.error('Unexpected error during admin logout:', error);
      // Force redirect to login on any unexpected error
      window.location.href = '/login';
    }
  };

  const handleNavigation = (path: string) => {
    if (isMobile) {
      setIsOpen(false);
    }
    navigate(path);
  };

  const sidebarContent = (
    <div className="space-y-4 py-2">
      <div className="px-3 py-1">
        <h2 className={cn(
          "mb-2 px-4 text-lg font-semibold tracking-tight text-primary",
          isCollapsed && !isMobile && "hidden"
        )}>
          Panel de Administración
        </h2>
        <div className="space-y-1">
          <NavLink to="/admin" end>
            {({ isActive }) => (
              <Button
                variant={isActive ? "secondary" : "ghost"}
                className="w-full justify-start"
                onClick={() => handleNavigation('/admin')}
              >
                <LayoutDashboard className="h-5 w-5" />
                {(!isCollapsed || isMobile) && <span className="ml-2">Dashboard</span>}
              </Button>
            )}
          </NavLink>

          <NavLink to="/dashboard">
            {({ isActive }) => (
              <Button
                variant={isActive ? "secondary" : "ghost"}
                className="w-full justify-start"
                onClick={() => handleNavigation('/dashboard')}
              >
                <User className="h-5 w-5" />
                {(!isCollapsed || isMobile) && <span className="ml-2">Dashboard Kid</span>}
              </Button>
            )}
          </NavLink>

          <NavLink to="/admin/users">
            {({ isActive }) => (
              <Button
                variant={isActive ? "secondary" : "ghost"}
                className="w-full justify-start"
                onClick={() => handleNavigation('/admin/users')}
              >
                <Users className="h-5 w-5" />
                {(!isCollapsed || isMobile) && <span className="ml-2">Usuarios</span>}
              </Button>
            )}
          </NavLink>

          <NavLink to="/admin/partners">
            {({ isActive }) => (
              <Button
                variant={isActive ? "secondary" : "ghost"}
                className="w-full justify-start"
                onClick={() => handleNavigation('/admin/partners')}
              >
                <Handshake className="h-5 w-5" />
                {(!isCollapsed || isMobile) && <span className="ml-2">Socios</span>}
              </Button>
            )}
          </NavLink>

          <NavLink to="/admin/lessons">
            {({ isActive }) => (
              <Button
                variant={isActive ? "secondary" : "ghost"}
                className="w-full justify-start"
                onClick={() => handleNavigation('/admin/lessons')}
              >
                <BookOpen className="h-5 w-5" />
                {(!isCollapsed || isMobile) && <span className="ml-2">Lecciones</span>}
              </Button>
            )}
          </NavLink>

          <NavLink to="/admin/library">
            {({ isActive }) => (
              <Button
                variant={isActive ? "secondary" : "ghost"}
                className="w-full justify-start"
                onClick={() => handleNavigation('/admin/library')}
              >
                <Book className="h-5 w-5" />
                {(!isCollapsed || isMobile) && <span className="ml-2">Biblioteca Digital</span>}
              </Button>
            )}
          </NavLink>

          <NavLink to="/admin/community">
            {({ isActive }) => (
              <Button
                variant={isActive ? "secondary" : "ghost"}
                className="w-full justify-start"
                onClick={() => handleNavigation('/admin/community')}
              >
                <UsersIcon className="h-5 w-5" />
                {(!isCollapsed || isMobile) && <span className="ml-2">Comunidad</span>}
              </Button>
            )}
          </NavLink>

          <NavLink to="/admin/settings">
            {({ isActive }) => (
              <Button
                variant={isActive ? "secondary" : "ghost"}
                className="w-full justify-start"
                onClick={() => handleNavigation('/admin/settings')}
              >
                <Wallet className="h-5 w-5" />
                {(!isCollapsed || isMobile) && <span className="ml-2">Finanzas</span>}
              </Button>
            )}
          </NavLink>

          <Button
            variant="ghost"
            className="w-full justify-start text-red-500 hover:text-red-600 hover:bg-red-50"
            onClick={handleLogout}
          >
            <LogOut className="h-5 w-5" />
            {(!isCollapsed || isMobile) && <span className="ml-2">Cerrar Sesión</span>}
          </Button>
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <>
        <Button
          variant="ghost"
          size="icon"
          className="fixed top-4 left-4 z-50 md:hidden"
          onClick={() => setIsOpen(true)}
        >
          <Menu className="h-6 w-6" />
        </Button>

        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetContent side="left" className="p-0 w-[280px]">
            {sidebarContent}
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <div className={cn(
      "h-screen border-r bg-background sticky top-0 overflow-y-auto hidden md:block",
      isCollapsed ? "w-16" : "w-64"
    )}>
      {sidebarContent}
    </div>
  );
};